/**
 * Scroll-snap carousel (2026 layout, home page "Latest News").
 *
 * Markup: [data-hc-carousel] > [data-hc-track] (a list of cards) + [data-hc-controls hidden]
 * with [data-hc-prev], [data-hc-next] and [data-hc-dot="i"] buttons.
 *
 * The track is a normal horizontally scrolling list (CSS scroll-snap), so every card stays in the
 * tab order and works without JS. This script only shows the controls when the track overflows,
 * scrolls one card per arrow press, and keeps the dots and disabled states in sync.
 */
export class ScrollCarousel {
    private readonly root: HTMLElement;
    private readonly track: HTMLElement;
    private readonly controls: HTMLElement | null;
    private readonly prev: HTMLButtonElement | null;
    private readonly next: HTMLButtonElement | null;
    private readonly dots: HTMLButtonElement[];
    private frame = 0;

    constructor(root: HTMLElement) {
        this.root = root;
        this.track = root.querySelector<HTMLElement>('[data-hc-track]')!;
        this.controls = root.querySelector<HTMLElement>('[data-hc-controls]');
        this.prev = root.querySelector<HTMLButtonElement>('[data-hc-prev]');
        this.next = root.querySelector<HTMLButtonElement>('[data-hc-next]');
        this.dots = Array.from(root.querySelectorAll<HTMLButtonElement>('[data-hc-dot]'));
        this.init();
    }

    private get slides(): HTMLElement[] {
        return Array.from(this.track.children) as HTMLElement[];
    }

    private get behavior(): ScrollBehavior {
        return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    }

    private init(): void {
        if (!this.track || this.slides.length === 0) return;

        this.prev?.addEventListener('click', () => this.goTo(this.activeIndex() - 1));
        this.next?.addEventListener('click', () => this.goTo(this.activeIndex() + 1));
        this.dots.forEach((dot, idx) => dot.addEventListener('click', () => this.goTo(idx)));

        this.track.addEventListener('scroll', () => this.schedule(), { passive: true });

        if (typeof ResizeObserver !== 'undefined') {
            new ResizeObserver(() => this.schedule()).observe(this.track);
        } else {
            window.addEventListener('resize', () => this.schedule());
        }

        this.update();
    }

    private schedule(): void {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
    }

    private overflows(): boolean {
        return this.track.scrollWidth > this.track.clientWidth + 2;
    }

    /** Index of the slide whose left edge is closest to the track's scroll position. */
    private activeIndex(): number {
        const left = this.track.scrollLeft;
        const origin = this.slides[0].offsetLeft;
        let best = 0;
        let bestDistance = Number.POSITIVE_INFINITY;

        this.slides.forEach((slide, idx) => {
            const distance = Math.abs(slide.offsetLeft - origin - left);
            if (distance < bestDistance) {
                best = idx;
                bestDistance = distance;
            }
        });

        // At the very end the last slide may never reach the left edge.
        if (left + this.track.clientWidth >= this.track.scrollWidth - 2) {
            best = this.slides.length - 1;
        }

        return best;
    }

    private goTo(index: number): void {
        const slides = this.slides;
        const target = Math.max(0, Math.min(index, slides.length - 1));
        this.track.scrollTo({ left: slides[target].offsetLeft - slides[0].offsetLeft, behavior: this.behavior });
    }

    private update(): void {
        const overflowing = this.overflows();
        this.root.classList.toggle('is-scrollable', overflowing);

        if (this.controls) {
            this.controls.hidden = !overflowing;
        }

        if (!overflowing) return;

        const active = this.activeIndex();
        const atStart = this.track.scrollLeft <= 2;
        const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 2;

        if (this.prev) this.prev.disabled = atStart;
        if (this.next) this.next.disabled = atEnd;

        this.dots.forEach((dot, idx) => {
            if (idx === active) dot.setAttribute('aria-current', 'true');
            else dot.removeAttribute('aria-current');
        });
    }
}

document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll<HTMLElement>('[data-hc-carousel]').forEach(root => new ScrollCarousel(root));
});
